import {
  FilterV2,
  OperatorType,
  ReportGroupType,
} from '@amzn/claritygqllambda';
import ClarityClient from '@clarity-website/lib/clients/ClarityClient';
import useClarityClient from '@clarity-website/lib/clients/useClarityClient';
import {
  PreferenceType,
  getUISetting,
} from '@clarity-website/queries/UISettingQueries';
import { setSelectedDynamicLeaderQuery } from '@clarity-website/reports/ReportPage/useDynamicLeaderSelection';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

export const dynamicSiteKey = 'dynamicSite';

/**
 * Reads the persisted comma-joined site IDs from DynamoDB for the given domain.
 * Returns an array of site ID strings, or undefined if not yet loaded.
 */
export const useGetSelectedDynamicSite = (
  domain: string,
): string[] | undefined => {
  const client = useClarityClient();
  const { data } = useQuery([dynamicSiteKey, domain], () =>
    getUISetting(client, `${dynamicSiteKey}#${domain}`),
  );
  if (!data) return undefined;
  return data
    .split(',')
    .map((s: string) => s.trim())
    .filter(Boolean);
};

/**
 * Persists site IDs as a comma-separated string in DynamoDB under key dynamicSite#<domain>
 * for each provided domain.
 */
export const updateSelectedDynamicSite = async (
  domains: string[],
  siteIds: string[],
  client: ClarityClient,
): Promise<boolean> => {
  const batchDynamicSiteSettings = domains.map((currDomain) => ({
    key: `${dynamicSiteKey}#${currDomain}`,
    setting: {
      preferenceType: PreferenceType.String,
      stringPreference: siteIds.join(','),
    },
  }));
  return client
    .gql({
      // Reuse the same setBatchUISettings mutation shape as leader
      request: setSelectedDynamicLeaderQuery,
      variables: { settings: batchDynamicSiteSettings },
    })
    .then((data) => data?.setBatchUISettings);
};

type DynamicSiteMutationInput = {
  domains: string[];
  siteIds: string[];
};

/**
 * React Query mutation hook that persists site selections with optimistic cache update.
 */
export function useDynamicSiteMutation() {
  const client = useClarityClient();
  const queryCache = useQueryClient();

  return useMutation(
    (input: DynamicSiteMutationInput) =>
      updateSelectedDynamicSite(input.domains, input.siteIds, client),
    {
      onMutate: async ({ domains, siteIds }) => {
        const joinedValue = siteIds.join(',');
        for (const domain of domains) {
          await queryCache.cancelQueries([dynamicSiteKey, domain]);
          queryCache.setQueryData([dynamicSiteKey, domain], joinedValue);
        }
      },
    },
  );
}

/**
 * Generates a FilterV2 array for the site filter.
 * Returns [] if:
 *   - reportGroupType is not DynamicFilter
 *   - no siteAttrId provided
 *   - no sites selected in selectionMap
 */
export function useCreateSiteFilter(
  domains?: string[],
  reportGroupType?: string,
  siteAttrId?: string | string[],
  selectionMap?: Record<string, string[]>,
): FilterV2[] {
  return useMemo((): FilterV2[] => {
    const selectedSiteIds = selectionMap?.['site'] ?? [];

    if (
      reportGroupType !== ReportGroupType.DynamicFilter ||
      !siteAttrId ||
      selectedSiteIds.length === 0 ||
      !domains ||
      domains.length === 0
    ) {
      return [];
    }

    if (Array.isArray(siteAttrId)) {
      return siteAttrId.map(
        (attrId) =>
          ({
            [OperatorType.And]: [
              {
                [OperatorType.In]: {
                  [attrId]: selectedSiteIds,
                },
              },
            ],
          } as FilterV2),
      );
    }

    return [
      {
        [OperatorType.And]: [
          {
            [OperatorType.In]: {
              [siteAttrId]: selectedSiteIds,
            },
          },
        ],
      } as FilterV2,
    ];
  }, [reportGroupType, siteAttrId, selectionMap, domains]);
}
